import "./style.scss";

import PagesHeader from "@components/PagesHeader";

import { useState, useRef } from "react";

import { useQuery } from "@tanstack/react-query";
import {
  DETAILS_CACHE_TIME,
  DETAILS_STALE_TIME,
  getUserDetails,
} from "@api/consumer";
import Button from "@components/Button";
import ContestForm from "./ContestForm";
import ContextCompletedForm from "./ContestCompletedForm";
import ContestAgeGate from "./ContestAreGate";
import GloLogo from "./img/glo-logo.svg";
import award01 from "./img/01-award.png";
import award02 from "./img/02-award.png";
import award03 from "./img/03-award.png";

import feature01 from "./img/01-feature.svg";
import feature02 from "./img/02-feature.svg";
import feature03 from "./img/03-feature.svg";
import feature04 from "./img/04-feature.svg";

import info01 from "./img/01-info.svg";
import info02 from "./img/02-info.svg";
import info03 from "./img/03-info.svg";
import info04 from "./img/04-info.svg";
import info05 from "./img/05-info.svg";

import {
  ACTIVATION_CACHE_TIME,
  ACTIVATION_STALE_TIME,
  getActivationDescribe,
} from "@api/activation";
import SectionHeader from "@components/SectionHeader";
import ContestFinish from "./ContestFinish";
import DOMPurify from "dompurify";

// const testContestValidationRules = {
//   required: {
//     value: true,
//     message: "Consent required",
//   },
// };

const infoItems = [
  {
    icon: info01,
    title: "relevant sein.",
    description:
      "Die glo-Story soll deine eigene Erfahrung mit glo™ beschreiben und sich nicht auf unsere Sticks oder andere Marken beziehen.",
  },
  {
    icon: info02,
    title: "authentisch sein.",
    description:
      "Wir interessieren uns für deine persönliche Meinung und Erfahrungen mit glo™ Produkten.",
  },
  {
    icon: info03,
    title: "klar und verständlich sein.",
    description:
      "Wir können deine glo-Story vor der Veröffentlichung geringfügig anpassen, ohne jedoch den Sinn des eingereichten Textes zu verfälschen.",
  },
  {
    icon: info04,
    title: "25 Jahre oder älter.",
    description:
      "Du musst 25 Jahre oder älter sein und glo™ seit mindestens 6 Monaten benutzen.",
  },
  {
    icon: info05,
    title: "Nicht gesundheitsbezogen sein.",
    description:
      "Teile deine glo™ Alltagserfahrung und was dir daran gefällt, aber keine persönlichen gesundheitsbezogenen Kommentare.",
  },
];

const features = [
  {
    icon: feature01,
    title: "Erhitzen statt<br/> Verbrennen*",
  },
  {
    icon: feature02,
    title: "Kein <br/> Rauchgeruch*",
  },
  {
    icon: feature03,
    title: "90-95% weniger<br/> Schadstoffe*",
  },
  {
    icon: feature04,
    title: "Keine <br/>Asche*",
  },
];

const awards = [
  {
    counter: "1",
    title: "Tomorrowland-Erlebnis <br/>für 2 Personen",
    image: award01,
  },
  {
    counter: "7",
    title: "iPhone 16 Pro",
    image: award02,
  },
  {
    counter: "50",
    title: "Amazon Gutschein <br/>für 50€",
    image: award03,
  },
];

const isAtLeast25YearsOld = (dob) => {
  const birthDate = new Date(dob);
  const today = new Date();
  const age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  const dayDiff = today.getDate() - birthDate.getDate();

  return (
    age > 25 ||
    (age === 25 && (monthDiff > 0 || (monthDiff === 0 && dayDiff >= 0)))
  );
};

export default function YourGloStory() {
  const ident = "thr_testimonials";

  const contestRef = useRef(null);

  const { data: contestStatus, refetch: refetchStatus } = useQuery({
    queryKey: ["activation-describe", ident],
    queryFn: () =>
      getActivationDescribe({
        activation: {
          ident: ident,
        },
      }),
    retry: false,
    cacheTime: ACTIVATION_CACHE_TIME,
    staleTime: ACTIVATION_STALE_TIME,
  });

  const dob = useQuery({
    queryKey: ["user-details"],
    queryFn: () => getUserDetails(),
    staleTime: DETAILS_STALE_TIME,
    cacheTime: DETAILS_CACHE_TIME,
    suspense: false,
    select: (data) => {
      return data?.consumer?.dob;
    },
  });

  const isFinish = contestStatus?.expired;

  if (isFinish) {
    return <ContestFinish />;
  }

  const userIsOver25 = isAtLeast25YearsOld(dob?.data);
  const contestIsCompleted = contestStatus?.completed;
  const answers = contestStatus?.answer ?? {};

  const handleScrollToContest = () => {
    contestRef.current.scrollIntoView({ behavior: "smooth", top: -50 });
  };

  return (
    <section className="your-glo-story-page">
      <PagesHeader>
        <strong>Deine glo-Story </strong>
      </PagesHeader>

      <div className="hero-section">
        <div className="content">
          <img src={GloLogo} alt="glo" className="logo" />
          <h2 className="title">
            Wir möchten <br />
            deine persönliche{" "}
            <span>
              <br />
              glo-Story hören!
            </span>
          </h2>
          <p className="desc">
            Teile deine Erfahrungen und gewinne Preise im{" "}
            <strong>Wert von 20.000€</strong>
          </p>
          <Button
            action={() => {
              handleScrollToContest();
            }}
          >
            Erzähl deine Story
          </Button>
        </div>
      </div>
      <div className="awards-section">
        <SectionHeader>
          <strong>
            {" "}
            Erzähl uns deine <br />
            glo-Story und gewinne
          </strong>
        </SectionHeader>
        <div className="awards-list">
          {awards.map((award, i) => (
            <div className="award" key={i}>
              <div className="award-header">
                <img src={award.image} alt="award" />
              </div>
              <div className="award-content">
                <div className="counter">{award.counter}x</div>

                <p
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(award.title)}}
                  className="award-title"
                />
              </div>
            </div>
          ))}
        </div>
        <div className="button-wrapper">
          <Button
            action={() => {
              handleScrollToContest();
            }}
          >
            Erzähl deine Story
          </Button>
        </div>
      </div>
      <div className="features-wrapper">
        <SectionHeader> Warum lieben wir glo?</SectionHeader>
        <div className="features-section">
          <h2 className="features-title">Was uns an glo gefällt</h2>
          <div className="features-list">
            {features.map((feature, i) => (
              <div className="feature" key={i}>
                <img src={feature.icon} alt="feature" />
                <h3
                  className="feature-title"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(feature.title)}}
                />
              </div>
            ))}
          </div>
          <div className="disclaimers">
            <p>
              Dies sind nur einige der Vorteile, die du mit glo genießen kannst.
              Wir alle mögen verschiedene Dinge daran!
            </p>
            <p>
              <strong>Was magst DU am meisten an glo?</strong>
            </p>
          </div>
        </div>
      </div>
      <div ref={contestRef}>
        <div>
          {userIsOver25 ? (
            <div className="info-section">
              <SectionHeader>
                {" "}
                <strong>
                  Erzähl deine persönliche
                  <br /> glo-Story und inspiriere andere
                </strong>
              </SectionHeader>
              <div className="grid-bottom">
                <div className="info-wrapper">
                  <h3 className="info-title">Deine glo-Story muss...</h3>
                  <div className="info-list">
                    {infoItems.map((item, i) => (
                      <div className="info-item" key={i}>
                        <div className="info-header">
                          <img src={item.icon} alt="info" />
                          <h4 className="info-item-title">{item.title}</h4>
                        </div>
                        <p className="info-item-desc">{item.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
                {contestIsCompleted ? (
                  <ContextCompletedForm answers={answers} />
                ) : (
                  <ContestForm refetchStatus={refetchStatus} />
                )}

                <div className="disclaimer-bottom">
                  *glo™ erhitzt Tabak anstatt ihn zu verbrennen. Dabei entsteht
                  ein Tabakaerosol, weniger Geruch und keine Asche. Vergleich
                  einer Standard-Referenzzigarette (ca. 9mg Teer) und der
                  Emissionen von glo™, in Bezug auf den Durchschnitt der 9
                  schädlichen Stoffe in Zigarettenrauch, die die
                  Weltgesundheitsorganisation empfiehlt zu reduzieren. Dieses
                  Produkt ist nicht risikofrei und enthält Nikotin, eine
                  abhängig machende Substanz.
                </div>
              </div>
            </div>
          ) : (
            <ContestAgeGate />
          )}
        </div>
      </div>
    </section>
  );
}
